import {
	ButtonArrowIcon,
	newCloseIcon,
} from "@/comps/ai-tools/icons"
import Button from "@/comps/button"
import { DropDownWithState } from "@/comps/dropdown"
import { CustomThemedResource } from "@/comps/image"
import { assetUrl } from "@/utils/cdn"
import { useShortCut } from "@kartyom/shared-components"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import {
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { InputsRequestType, SelectorType } from "./popups"

type GenderType = "male" | "female" | null

interface StylesPopupProps {
	isOpen: boolean
	setIsOpen: (a: boolean) => void
	selectors: SelectorType[]
	selectedGender: GenderType
	inputsRequest: InputsRequestType
	submitData: () => void
}

export function StylesPopup(props: StylesPopupProps) {
	const {
		setIsOpen,
		isOpen,
		selectors,
		inputsRequest,
		selectedGender,
		submitData,
	} = props

	const { t } = useTranslation()
	const [musicPopup, setMusicPopup] = useState(false)
	const [voicePopup, setVoicePopup] = useState(false)
	const [captionsPopup, setCaptionsPopup] = useState(false)

	const actorsSelector = selectors[0]
	const voiceoverSelector = selectors[1]
	const musicSelector = selectors[2]
	const ratioSelector = selectors[3]
	const captionsSelector = selectors[4]

	useShortCut({
		shortCut: "Escape",
		handler: () => {
			if (isOpen) setIsOpen(false)
		},
	})
	return (
		<>
			<div
				id="styles-popup"
				onClick={(e) => {
					const target = e.currentTarget
					if (target instanceof HTMLElement) {
						if (target.id === "styles-popup") {
							setIsOpen(false)
						}
					}
				}}
				className={clsx(
					"fixed z-[103] h-[100svh] w-screen items-end tablet:items-center",
					"left-0 top-0 flex justify-center transition-all",
					!isOpen && "pointer-events-none opacity-0",
				)}></div>
			<div
				onClick={(e) => e.stopPropagation()}
				className={clsx(
					"fixed bottom-0 left-0 z-[103]",
					"flex flex-col bg-color-surface-cell tablet:pb-0",
					"transition-transform duration-300 tablet:transition-opacity",
					"max-h-[90svh] w-full tablet:h-[631px] tablet:w-[700px] desktop:h-[631px] desktop:w-[934px]",
					"rounded-t-[10px] border border-color-separator tablet:rounded-[10px]",
					"tablet:bottom-1/2 tablet:left-1/2 tablet:-translate-x-1/2 tablet:translate-y-1/2",
					!isOpen &&
						"pointer-events-none translate-y-full tablet:translate-y-0 tablet:opacity-0",
				)}>
				{/* Title Section */}
				<div className="flex h-[69px] w-full shrink-0 flex-row items-center justify-between">
					<button
						onClick={(e) => {
							e.preventDefault()
							setIsOpen(false)
						}}
						className="pl-[14px] [--icon-color:var(--color-blue-800)] tablet:pl-[15px]">
						<svg
							width="28"
							height="28"
							viewBox="0 0 28 28"
							fill="none"
							xmlns="http://www.w3.org/2000/svg">
							<path
								d="M16.334 21L9.33398 14L16.334 7"
								stroke="var(--icon-color)"
								strokeWidth="2.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
					<span className="text-[20px] font-700 text-blue-800 tablet:text-[24px]">
						{t("txt_edit_style")}
					</span>
					<div className="w-[42px] tablet:w-[43px]" />
				</div>
				{/* Selectors Section */}
				<div className="no-scrollbar flex w-full flex-col gap-5 overflow-y-auto pb-[95px] tablet:h-[464px] tablet:max-h-[464px] tablet:flex-row tablet:gap-0 tablet:overflow-auto tablet:pb-0 tablet:pl-5 desktop:overflow-visible">
					<Actors
						selector={actorsSelector}
						selected={inputsRequest.actors.id}
					/>

					<div className="no-scrollbar flex flex-1 flex-col gap-5 tablet:w-[282px] tablet:overflow-y-auto tablet:px-5 desktop:w-[327px] desktop:overflow-visible">
						<CaptionsDropdown
							selector={captionsSelector}
							selected={inputsRequest.captions}
							setIsOpen={setCaptionsPopup}
						/>
						<VoiceoverDropdown
							selector={voiceoverSelector}
							selectedGender={selectedGender}
							selected={inputsRequest.voices}
							setIsOpen={setVoicePopup}
						/>

						<MusicDropdown
							selector={musicSelector}
							selected={inputsRequest.music}
							setIsOpen={setMusicPopup}
						/>

						<Aspects
							selector={ratioSelector}
							selected={inputsRequest.aspects}
						/>
					</div>
				</div>

				<div className="fixed bottom-0 left-0 flex w-full items-center justify-center bg-color-surface-cell px-4 py-5 tablet:relative tablet:bg-[unset] tablet:px-[30px] tablet:py-6">
					<Button
						primary
						onClick={submitData}
						text="txt_submit"
						className="!h-[50px] !w-full !min-w-0 desktop:!w-[340px]"
					/>
				</div>
			</div>

			<CaptionsPopup
				isOpen={captionsPopup}
				setIsOpen={setCaptionsPopup}
				selected={inputsRequest.captions}
				selector={captionsSelector}
			/>

			<MusicPopup
				isOpen={musicPopup}
				setIsOpen={setMusicPopup}
				selector={musicSelector}
				selected={inputsRequest.music}
			/>
			<VoicePopup
				isOpen={voicePopup}
				setIsOpen={setVoicePopup}
				selector={voiceoverSelector}
				selected={inputsRequest.voices}
				selectedGender={selectedGender}
			/>
		</>
	)
}

interface CaptionsPopupProps extends CaptionsDropdownProps {
	isOpen: boolean
}
function CaptionsPopup(props: CaptionsPopupProps) {
	const { isOpen, setIsOpen, selector, selected } = props
	const { t } = useTranslation()

	if (selector.type !== "captions") return <></>

	return (
		<>
			<div
				id="captions-popup"
				onClick={(e) => {
					const target = e.currentTarget
					if (target instanceof HTMLElement) {
						if (target.id === "captions-popup") {
							setIsOpen(false)
						}
					}
				}}
				className={clsx(
					"fixed z-[103] h-[100svh] w-screen items-end tablet:hidden",
					"left-0 top-0 flex justify-center bg-color-popup transition-all",
					!isOpen && "pointer-events-none opacity-0",
				)}></div>
			<div
				onClick={(e) => e.stopPropagation()}
				className={clsx(
					"fixed bottom-0 left-0 z-[103] tablet:hidden",
					"flex flex-col gap-1 bg-color-surface-cell",
					"px-2 pt-3 transition-transform duration-300",
					"max-h-[60svh] w-screen rounded-t-[10px]",
					!isOpen && "pointer-events-none translate-y-full",
				)}>
				<div className="flex h-8 w-full items-center justify-between px-2">
					<span className="text-[16px] font-600 text-blue-800">
						{t(selector.name)}
					</span>
					<button
						onClick={(e) => {
							e.preventDefault()
							setIsOpen(false)
						}}
						className="[--icon-color:var(--color-blue-800)]">
						{newCloseIcon("20", "20")}
					</button>
				</div>
				<div className="no-scrollbar grid grid-cols-2 gap-3 overflow-y-auto py-3">
					{selector.info.data.map((s) => {
						const { thumbnail, id } = s
						if (!thumbnail || !id) return

						return (
							<button
								key={id}
								onClick={(e) => {
									e.preventDefault()
									selector.action(id, thumbnail)
									setIsOpen(false)
								}}
								className={clsx(
									"flex h-[60px] max-h-[60px] w-full shrink-0 items-center justify-center rounded-lg px-2",
									"bg-blue-100 py-1 transition-all",
									selected.id === id &&
										"border-2 border-primary-500 bg-primary-100",
								)}>
								<img
									src={thumbnail}
									alt="Caption image"
									className="h-full"
								/>
							</button>
						)
					})}
				</div>
			</div>
		</>
	)
}

interface CaptionsDropdownProps {
	selector: SelectorType
	selected: {
		id: string
		thumb_url: string
	}
	setIsOpen: (v: boolean) => void
}

function CaptionsDropdown(props: CaptionsDropdownProps) {
	const { selector, selected, setIsOpen } = props
	const { t } = useTranslation()
	const [dropdownOpen, setDropdownOpen] = useState(false)
	if (selector.type !== "captions") return <></>

	return (
		<div className="flex flex-col gap-2 px-5 tablet:px-0">
			<span className="text-[17px] font-400 text-blue-500">
				{t(selector.name)}
			</span>

			<button
				onClick={(e) => {
					e.preventDefault()
					setIsOpen(true)
				}}
				className={clsx(
					"flex h-[56px] w-full items-center justify-between tablet:hidden",
					"gap-[8px] rounded-[6px] bg-color-surface px-[8px]",
				)}>
				<div className="w-3 shrink-0" />
				<div className="flex h-full flex-1 items-center justify-center gap-[2px] py-1">
					<img
						src={selected.thumb_url}
						alt="Caption image"
						className="h-full"
					/>
				</div>
				<div className="flex items-center gap-[2px]">
					<CustomThemedResource
						source="/general/arrow"
						format="svg"
						className="h-[12px] w-[12px] rotate-90 transition-all group-focus-within:-rotate-90"
					/>
				</div>
			</button>

			<DropDownWithState
				key={selector.info.type}
				isOpen={dropdownOpen}
				setIsOpen={setDropdownOpen}
				className="group z-[69] hidden tablet:block"
				triggerClassName="w-full"
				trigger={
					<div
						className={clsx(
							"flex h-[56px] w-full items-center justify-between",
							"gap-[8px] rounded-[6px] bg-color-surface px-[8px]",
						)}>
						<div className="w-5 shrink-0" />
						<div className="flex h-full flex-1 items-center justify-center gap-[2px] py-1">
							<img
								src={selected.thumb_url}
								alt="Caption image"
								className="h-full"
							/>
						</div>
						<div className="flex items-center gap-[2px]">
							<CustomThemedResource
								source="/general/arrow"
								format="svg"
								className={clsx(
									"h-[20px] w-[20px] transition-all",
									dropdownOpen ? "-rotate-90" : "rotate-90",
								)}
							/>
						</div>
					</div>
				}>
				<div
					className={clsx(
						"absolute left-0 top-[62px] z-30 flex bg-color-cell",
						"h-[350px] flex-col rounded-[6px] border border-color-separator p-[6px]",
						"no-scrollbar w-full gap-[2px] overflow-y-scroll [box-shadow:0px_0px_10px_rgba(0,0,0,0.13)]",
					)}>
					{selector.info.data.map((s) => {
						const { thumbnail, id } = s
						if (!thumbnail || !id) return

						return (
							<button
								key={id}
								onClick={(e) => {
									e.preventDefault()
									setDropdownOpen(false)
									selector.action(id, thumbnail)
								}}
								className={clsx(
									"flex h-[60px] max-h-[60px] shrink-0 items-center justify-center rounded-md px-2 py-1",
									"hover:bg-color-surface",
									selected.id === id && "bg-color-surface",
								)}>
								<img
									src={thumbnail}
									alt="Caption image"
									className="h-full"
								/>
							</button>
						)
					})}
				</div>
			</DropDownWithState>
		</div>
	)
}

interface VoicePopupProps extends VoiceoverProps {
	isOpen: boolean
}
function VoicePopup(props: VoicePopupProps) {
	const {
		isOpen,
		setIsOpen,
		selector,
		selected,
		selectedGender,
	} = props
	const { t } = useTranslation()

	const [selectedAudio, setSelectedAudio] =
		useState<HTMLAudioElement | null>(null)

	if (selector.type !== "voices") return <></>

	return (
		<>
			<div
				id="voice-popup"
				onClick={(e) => {
					const target = e.currentTarget
					if (target instanceof HTMLElement) {
						if (target.id === "voice-popup") {
							setIsOpen(false)
						}
					}
				}}
				className={clsx(
					"fixed z-[103] h-[100svh] w-screen items-end tablet:hidden",
					"left-0 top-0 flex justify-center bg-color-popup transition-all",
					!isOpen && "pointer-events-none opacity-0",
				)}></div>
			<div
				onClick={(e) => e.stopPropagation()}
				className={clsx(
					"fixed bottom-0 left-0 z-[103] tablet:hidden",
					"flex flex-col gap-1 bg-color-surface-cell",
					"px-2 pt-3 transition-transform duration-300",
					"max-h-[50svh] w-screen rounded-t-[10px]",
					!isOpen && "pointer-events-none translate-y-full",
				)}>
				<div className=" flex h-8 w-full items-center justify-between px-2">
					<span className="text-[16px] font-600 text-blue-800">
						{t(selector.name)}
					</span>
					<button
						onClick={(e) => {
							e.preventDefault()
							setIsOpen(false)
						}}
						className="[--icon-color:var(--color-blue-800)]">
						{newCloseIcon("20", "20")}
					</button>
				</div>
				{isOpen && (
					<div className="no-scrollbar flex flex-col gap-1 overflow-y-auto py-3">
						{selector.info.data
							.filter(
								(elem) =>
									elem.gender === selectedGender ||
									!selectedGender,
							)
							.map((s) => (
								<Voiceover
									key={s.id}
									voice={s}
									isSelected={s.id === selected.id}
									setSelectedAudio={setSelectedAudio}
									selector={selector}
									selectedAudio={selectedAudio}
									onClick={() => setIsOpen(false)}
								/>
							))}
					</div>
				)}
			</div>
		</>
	)
}

interface MusicPopupProps extends MusicDropdownProps {
	isOpen: boolean
}
function MusicPopup(props: MusicPopupProps) {
	const { isOpen, setIsOpen, selector, selected } = props
	const { t } = useTranslation()

	if (selector.type !== "music") return <></>

	return (
		<>
			<div
				id="music-popup"
				onClick={(e) => {
					const target = e.currentTarget
					if (target instanceof HTMLElement) {
						if (target.id === "music-popup") {
							setIsOpen(false)
						}
					}
				}}
				className={clsx(
					"fixed z-[103] h-[100svh] w-screen items-end tablet:hidden",
					"left-0 top-0 flex justify-center bg-color-popup transition-all",
					!isOpen && "pointer-events-none opacity-0",
				)}></div>
			<div
				onClick={(e) => e.stopPropagation()}
				className={clsx(
					"fixed bottom-0 left-0 z-[103] tablet:hidden",
					"flex flex-col gap-1 bg-color-surface-cell",
					"px-2 pt-3 transition-transform duration-300",
					"max-h-[50svh] w-screen rounded-t-[10px]",
					!isOpen && "pointer-events-none translate-y-full",
				)}>
				<div className="flex h-8 w-full items-center justify-between px-2">
					<span className="text-[16px] font-600 text-blue-800">
						{t(selector.name)}
					</span>
					<button
						onClick={(e) => {
							e.preventDefault()
							setIsOpen(false)
						}}
						className="[--icon-color:var(--color-blue-800)]">
						{newCloseIcon("20", "20")}
					</button>
				</div>
				<div className="no-scrollbar flex flex-col gap-1 overflow-y-auto py-3">
					{selector.info.data.map((music) => (
						<button
							key={music.id}
							onClick={(e) => {
								e.preventDefault()
								selector.action(music.id ?? "", music.name)
								setIsOpen(false)
							}}
							className={clsx(
								"flex w-full items-center justify-start p-2 text-[17px] font-400 text-blue-800",
								"rounded-md transition-colors hover:bg-color-surface",
								selected.name === music.name &&
									"bg-color-surface",
							)}>
							{music.name}
						</button>
					))}
				</div>
			</div>
		</>
	)
}

interface AspectsProps {
	selector: SelectorType
	selected: {
		id: string
		name: string
	}
}

function Aspects(props: AspectsProps) {
	const { selector, selected } = props
	const { t } = useTranslation()

	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const [prevDisabled, setPrevDisabled] = useState(true)
	const [nextDisabled, setNextDisabled] = useState(false)
	const scrollLeft = () => {
		if (!scrollContainerRef.current) {
			return
		}
		const prev = getPrevVisibleChild(
			scrollContainerRef.current,
		)
		if (!prev) {
			return
		}

		prev.scrollIntoView({
			block: "center",
			inline: "start",
			behavior: "smooth",
		})
	}

	const scrollRight = () => {
		if (!scrollContainerRef.current) {
			return
		}
		const next = getNextVisibleChild(
			scrollContainerRef.current,
		)
		if (!next) {
			return
		}

		next.scrollIntoView({
			block: "center",
			inline: "end",
			behavior: "smooth",
		})
	}

	const updateButtonStatus = useCallback(() => {
		let nextDisabled = false
		if (
			scrollContainerRef.current &&
			scrollContainerRef.current.scrollWidth <=
				scrollContainerRef.current.clientWidth
		) {
			nextDisabled = true
		}

		if (
			scrollContainerRef.current &&
			getNextVisibleChild(scrollContainerRef.current)
		) {
			nextDisabled = false
		}

		if (
			scrollContainerRef.current &&
			!getNextVisibleChild(scrollContainerRef.current)
		) {
			nextDisabled = true
		}

		setNextDisabled(nextDisabled)

		let prevDisabled = true
		if (
			scrollContainerRef.current &&
			getPrevVisibleChild(scrollContainerRef.current)
		) {
			prevDisabled = false
		}

		setPrevDisabled(prevDisabled)
	}, [scrollContainerRef])

	useEffect(() => {
		updateButtonStatus()
	}, [updateButtonStatus])

	if (selector.type !== "aspect") return <></>
	return (
		<div className="flex w-full flex-col gap-2">
			<span className="px-5 text-[17px] font-400 text-blue-500 tablet:px-0">
				{t(selector.name)}
			</span>

			<div className="relative">
				<div
					onScroll={updateButtonStatus}
					ref={scrollContainerRef}
					className="no-scrollbar flex flex-row gap-[6px] overflow-x-scroll rounded-lg px-5 tablet:px-0">
					{selector.info.data.map((aspect) => {
						const aspectSize = getAspectsSizes(aspect.name)
						return (
							<button
								key={aspect.id}
								onClick={(e) => {
									e.preventDefault()
									selector.action(
										aspect.id ?? "",
										aspect.name,
									)
								}}
								className={clsx(
									"flex h-10 shrink-0 flex-row items-center gap-1 rounded-lg px-[10px] py-3",
									"transition-all",
									selected.id === aspect.id
										? "bg-primary-50 inner-border-2 inner-border-primary-500"
										: "bg-color-surface inner-border inner-border-blue-200",
								)}>
								<div className="flex h-5 w-5 shrink-0 items-center justify-center">
									<div
										style={{
											width: aspectSize.width,
											height: aspectSize.height,
										}}
										className="rounded-[2.5px] border border-blue-400"></div>
								</div>
								{removeAspectsName(aspect.name)}
							</button>
						)
					})}
				</div>

				{/* Left Button */}

				<button
					id="scroll-left"
					disabled={prevDisabled}
					style={{
						filter:
							"drop-shadow(0px 2.353px 13.647px rgba(123, 128, 158, 0.12))",
					}}
					className={clsx(
						"absolute top-[calc(50%-11px)] hidden h-[22px] w-[22px]",
						"items-center justify-center rounded-full bg-color-cell",
						"[--icon-color:var(--color-blue-600)] disabled:pointer-events-none",
						"disabled:opacity-0 desktop:flex",
						"left-2 border border-color-separator",
					)}
					onClick={scrollLeft}>
					{ButtonArrowIcon()}
				</button>

				{/* Right Button */}

				<button
					id="scroll-right"
					disabled={nextDisabled}
					style={{
						filter:
							"drop-shadow(0px 2.353px 13.647px rgba(123, 128, 158, 0.12))",
					}}
					className={clsx(
						"absolute top-[calc(50%-11px)] hidden h-[22px] w-[22px]",
						"rotate-180 items-center justify-center rounded-full bg-color-cell",
						"[--icon-color:var(--color-blue-600)] disabled:pointer-events-none",
						"disabled:opacity-0 desktop:flex",
						"right-2 border border-color-separator",
					)}
					onClick={scrollRight}>
					{ButtonArrowIcon()}
				</button>
			</div>
		</div>
	)
}

function getAspectsSizes(name: string) {
	switch (name) {
		case "Portrait (9:16)":
			return { width: "14px", height: "20px" }
		case "Landscape (16:9)":
			return { width: "20px", height: "14px" }
		case "Square (1:1)":
			return { width: "20px", height: "20px" }
		case "4:5":
			return { width: "16px", height: "20px" }
		case "5:4":
			return { width: "20px", height: "16px" }
		default:
			return { width: "20px", height: "20px" }
	}
}

function removeAspectsName(name: string) {
	switch (name) {
		case "Portrait (9:16)":
			return "9:16"
		case "Landscape (16:9)":
			return "16:9"
		case "Square (1:1)":
			return "1:1"
		default:
			return name
	}
}

interface MusicDropdownProps {
	selector: SelectorType
	selected: {
		id: string | null
		name: string
	}
	setIsOpen: (v: boolean) => void
}

function MusicDropdown(props: MusicDropdownProps) {
	const { selector, selected, setIsOpen } = props
	const { t } = useTranslation()
	const [musicDropdown, setMusicDropdown] = useState(false)

	if (selector.type !== "music") return <></>

	return (
		<div className="flex flex-col gap-2 px-5 tablet:px-0">
			<span className="text-[17px] font-400 text-blue-500">
				{t(selector.name)}
			</span>

			<button
				onClick={(e) => {
					e.preventDefault()
					setIsOpen(true)
				}}
				className={clsx(
					"flex h-[48px] w-full items-center justify-between",
					"gap-[8px] rounded-[6px] bg-color-surface px-[8px] tablet:hidden",
				)}>
				<div className="flex items-center gap-[2px]">
					<span
						className={clsx(
							"max-w-[70px] truncate text-[15px] font-500 text-blue-800",
						)}>
						{selected.name}
					</span>
				</div>
				<div className="flex items-center gap-[2px]">
					<CustomThemedResource
						source="/general/arrow"
						format="svg"
						className="h-[12px] w-[12px] rotate-90 transition-all group-focus-within:-rotate-90"
					/>
				</div>
			</button>

			<DropDownWithState
				key={selector.info.type}
				isOpen={musicDropdown}
				setIsOpen={setMusicDropdown}
				className="group z-[67] hidden tablet:block"
				triggerClassName="w-full"
				trigger={
					<div
						className={clsx(
							"flex h-[48px] w-full items-center justify-between",
							"gap-[8px] rounded-[6px] bg-color-surface px-[8px]",
						)}>
						<div className="flex items-center gap-[2px]">
							<span
								className={clsx(
									"max-w-[70px] truncate text-[15px] font-500 text-blue-800",
								)}>
								{selected.name}
							</span>
						</div>
						<div className="flex items-center gap-[2px]">
							<CustomThemedResource
								source="/general/arrow"
								format="svg"
								className={clsx(
									"h-[20px] w-[20px] transition-all",
									musicDropdown
										? "-rotate-90"
										: "rotate-90",
								)}
							/>
						</div>
					</div>
				}>
				<div
					className={clsx(
						"absolute left-0 top-[55px] z-30 flex bg-color-cell",
						"h-[220px] flex-col rounded-[6px] border border-color-separator p-[6px]",
						"no-scrollbar w-full gap-[2px] overflow-y-scroll [box-shadow:0px_0px_10px_rgba(0,0,0,0.13)]",
					)}>
					{selector.info.data.map((s) => (
						<button
							key={s.id}
							onClick={(e) => {
								e.preventDefault()
								setMusicDropdown(false)
								selector.action(s.id ?? "", s.name)
							}}
							className={clsx(
								"flex items-center justify-start rounded-md px-2 py-2 text-[17px] font-400 text-blue-800",
								"hover:bg-color-surface",
								selected.name === s.name &&
									"bg-color-surface",
							)}>
							{s.name}
						</button>
					))}
				</div>
			</DropDownWithState>
		</div>
	)
}

interface ActorsProps {
	selector: SelectorType
	selected: string | null
}

function Actors(props: ActorsProps) {
	const { selector, selected } = props
	const { t } = useTranslation()

	const [thumbnails, setThumbnails] = useState(
		selector.info.data.map((actor) => actor.thumbnail),
	)

	// Function to update the thumbnail for a specific actor
	const updateThumbnail = (
		index: number,
		newThumbnail: string | null,
	) => {
		setThumbnails((prevThumbnails) => {
			const newThumbnails = [...prevThumbnails]
			newThumbnails[index] = newThumbnail
			return newThumbnails
		})
	}

	if (selector.type !== "actors") return <></>

	return (
		<div className="flex shrink-0 flex-col gap-[10px] tablet:h-full tablet:max-h-[464px] tablet:w-[362px] desktop:w-[547px]">
			<span className="h-5 px-5 text-[17px] font-400 text-blue-500 tablet:px-0">
				{t(selector.name)}
			</span>

			<div className="no-scrollbar flex flex-1 shrink-0 flex-row gap-2 overflow-x-scroll rounded-[12px] px-5 tablet:grid tablet:h-[434px] tablet:max-h-[434px] tablet:grid-cols-2 tablet:overflow-y-scroll tablet:px-0 desktop:grid-cols-3">
				{selector.info.data.map((actor, index) => {
					const thumb = thumbnails[index]

					return (
						<button
							key={actor.id}
							onPointerEnter={() => {
								updateThumbnail(
									index,
									actor.animated_thumbnail,
								)
							}}
							onPointerLeave={() => {
								if (selected === actor.id) return

								updateThumbnail(index, actor.thumbnail)
							}}
							onClick={(e) => {
								e.preventDefault()
								updateThumbnail(
									index,
									actor.animated_thumbnail,
								)
								selector.action(
									actor.id,
									actor.name,
									actor.thumbnail,
									actor.gender,
								)
							}}
							className={clsx(
								"h-[158px] w-[120px]",
								"shrink-0 rounded-[12px] tablet:h-[201px] tablet:w-full",
								"flex flex-col items-center justify-center gap-2 transition-all",
								selected === actor.id
									? "border-2 border-primary-500 bg-primary-50"
									: "border border-blue-200 bg-color-surface",
							)}>
							<div className="h-[90px] w-[90px] shrink-0 overflow-hidden rounded-full bg-blue-200 tablet:h-[130px] tablet:w-[130px]">
								{thumb ? (
									<img
										src={thumb}
										alt={actor.name + " image"}
										className="h-full w-full object-cover"
									/>
								) : (
									<div className="flex h-full w-full items-center justify-center [--icon-color:var(--color-blue-900)]">
										<NoActorImage />
									</div>
								)}
							</div>
							<span className="text-[18px] font-400 text-blue-700">
								{t(actor.name)}
							</span>
						</button>
					)
				})}
			</div>
		</div>
	)
}

interface VoiceoverProps {
	selector: SelectorType
	selectedGender: GenderType
	selected: {
		id: string
		name: string
		tags: string[]
		preview_url: string
		gender: GenderType
	}
	setIsOpen: (v: boolean) => void
}

function VoiceoverDropdown(props: VoiceoverProps) {
	const { selector, selectedGender, selected, setIsOpen } =
		props
	const { t } = useTranslation()

	const [selectedAudio, setSelectedAudio] =
		useState<HTMLAudioElement | null>(null)
	const audioRef = useRef<HTMLAudioElement>(null)
	const [playAudioButton, setPlayAudioButton] =
		useState(false)
	const [dropdown, setDropdown] = useState(false)

	useEffect(() => {
		let frameId: number | null
		function renderNextFrame() {
			const audio = audioRef.current
			if (!audio) {
				return
			}

			if (audio.currentTime === audio.duration) {
				audio.currentTime = 0
				audio.pause()
				setPlayAudioButton(false)
			}

			frameId = requestAnimationFrame(() => {
				renderNextFrame()
			})
		}

		renderNextFrame()

		return () => {
			if (frameId !== null) {
				cancelAnimationFrame(frameId)
			}
		}
	}, [audioRef])

	function handleAudioPlay() {
		if (!audioRef.current) {
			return
		}

		if (audioRef.current.paused) {
			audioRef.current.src = selected.preview_url
			setPlayAudioButton(true)
			audioRef.current
				.play()
				.catch(() => console.error("Cannot play"))
		} else {
			audioRef.current.src = selected.preview_url
			setPlayAudioButton(false)
			audioRef.current.pause()
		}
	}

	if (selector.type !== "voices") return <></>

	return (
		<div className="flex flex-col gap-2 px-5 tablet:px-0">
			<span className="text-[17px] font-400 text-blue-500">
				{t(selector.name)}
			</span>

			<div
				onClick={(e) => {
					e.preventDefault()
					setIsOpen(true)
				}}
				className={clsx(
					"flex h-[56px] w-full items-center justify-between",
					"gap-[8px] rounded-[6px] bg-color-surface px-[8px] tablet:hidden",
				)}>
				<div className="flex w-[70%] items-center justify-start gap-[2px]">
					<div
						className={clsx(
							"group items-center gap-[8px] p-[4px]",
							"flex w-full cursor-pointer rounded-[4px]",
						)}>
						<button
							onClick={(e) => {
								e.stopPropagation()
								handleAudioPlay()
							}}
							className={clsx(
								playAudioButton
									? "bg-color-chat-alpha"
									: "bg-blue-300",
								"shrink-0 rounded-full p-[7px]",
							)}>
							{playAudioButton ? (
								<img
									src={assetUrl("/general/pause-chat.png")}
									alt="play icon"
									className="h-[15px] w-[15px]"
								/>
							) : (
								<img
									src={assetUrl("/general/play.png")}
									alt="play icon"
									className="h-[15px] w-[15px]"
								/>
							)}
						</button>

						<div className="flex w-full flex-col items-start">
							<span className="w-full truncate text-[13px] font-500 text-blue-600">
								{selected.name}
							</span>

							{selected.tags && (
								<div className="flex w-full items-center truncate">
									{selected.tags.map((l, index) => (
										<div
											key={index}
											className="flex items-center">
											<span className="text-[13px] font-400 text-blue-600">
												{l}
											</span>
											{index + 1 !==
												selected.tags?.length && (
												<div className="mx-[4px] h-[4px] w-[4px] rounded-full bg-blue-300" />
											)}
										</div>
									))}
								</div>
							)}
						</div>

						<audio
							className="hidden"
							ref={audioRef}
							controls>
							<source
								src={selected.preview_url}
								type="audio/mpeg"
							/>
							Your browser does not support the audio
							element.
						</audio>
					</div>
				</div>
				<div className="flex items-center gap-[2px]">
					<CustomThemedResource
						source="/general/arrow"
						format="svg"
						className="h-[12px] w-[12px] rotate-90 transition-all group-focus-within:-rotate-90"
					/>
				</div>
			</div>

			<DropDownWithState
				key={selector.info.type}
				isOpen={dropdown}
				setIsOpen={setDropdown}
				className="group z-[68] hidden tablet:block"
				triggerClassName="w-full"
				trigger={
					<div
						className={clsx(
							"flex h-[56px] w-full items-center justify-between",
							"gap-[8px] rounded-[6px] bg-color-surface px-[8px]",
						)}>
						<div className="flex w-[70%] items-center justify-start gap-[2px]">
							<div
								className={clsx(
									"group items-center gap-[8px] p-[4px]",
									"flex w-full cursor-pointer rounded-[4px]",
								)}>
								<div
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										handleAudioPlay()
									}}
									className={clsx(
										playAudioButton
											? "bg-color-chat-alpha"
											: "bg-blue-300",
										"shrink-0 rounded-full p-[7px]",
									)}>
									{playAudioButton ? (
										<img
											src={assetUrl(
												"/general/pause-chat.png",
											)}
											alt="play icon"
											className="h-[15px] w-[15px]"
										/>
									) : (
										<img
											src={assetUrl("/general/play.png")}
											alt="play icon"
											className="h-[15px] w-[15px]"
										/>
									)}
								</div>

								<div className="flex w-full flex-col items-start">
									<span className="w-full truncate text-start text-[13px] font-500 text-blue-600">
										{selected.name}
									</span>

									{selected.tags && (
										<div className="flex w-full items-center truncate">
											{selected.tags.map((l, index) => (
												<div
													key={index}
													className="flex items-center">
													<span className="text-[13px] font-400 text-blue-600">
														{l}
													</span>
													{index + 1 !==
														selected.tags?.length && (
														<div className="mx-[4px] h-[4px] w-[4px] rounded-full bg-blue-300" />
													)}
												</div>
											))}
										</div>
									)}
								</div>

								<audio
									className="hidden"
									ref={audioRef}
									controls>
									<source
										src={selected.preview_url}
										type="audio/mpeg"
									/>
									Your browser does not support the audio
									element.
								</audio>
							</div>
						</div>
						<div className="flex items-center gap-[2px]">
							<CustomThemedResource
								source="/general/arrow"
								format="svg"
								className={clsx(
									"h-[20px] w-[20px] transition-all",
									dropdown ? "-rotate-90" : "rotate-90",
								)}
							/>
						</div>
					</div>
				}>
				<div
					className={clsx(
						"no-scrollbar absolute left-0 top-[62px] z-30 flex bg-color-cell",
						"h-[250px] flex-col rounded-[6px] border border-color-separator p-[6px]",
						"w-full gap-[2px] overflow-y-scroll [box-shadow:0px_0px_10px_rgba(0,0,0,0.13)]",
					)}>
					{selector.info.data
						.filter(
							(elem) =>
								elem.gender === selectedGender ||
								!selectedGender,
						)
						.map((s) => (
							<Voiceover
								key={s.id}
								voice={s}
								isSelected={s.id === selected.id}
								setSelectedAudio={setSelectedAudio}
								selector={selector}
								selectedAudio={selectedAudio}
								onClick={() => setDropdown(false)}
							/>
						))}
				</div>
			</DropDownWithState>
		</div>
	)
}

function Voiceover(props: {
	voice: {
		id: string | null
		name: string
		thumbnail: string | null
		animated_thumbnail: string | null
		preview_url: string | null
		tags: string[] | null
		gender: GenderType
	}
	selector: SelectorType
	selectedAudio: HTMLAudioElement | null
	isSelected: boolean
	setSelectedAudio: (v: HTMLAudioElement | null) => void
	onClick?: () => void
}) {
	const {
		selectedAudio,
		setSelectedAudio,
		isSelected,
		selector,
		voice,
		onClick,
	} = props

	const { id, name, tags, preview_url, gender } = voice
	const audioRef = useRef<HTMLAudioElement>(null)
	const [playAudioButton, setPlayAudioButton] =
		useState(false)

	useEffect(() => {
		let frameId: number | null
		function renderNextFrame() {
			const audio = audioRef.current
			if (!audio) {
				return
			}

			if (selectedAudio === null) {
				audio.pause()
			}

			if (audio.currentTime === audio.duration) {
				audio.currentTime = 0
				audio.pause()
				setPlayAudioButton(false)
			}

			if (audio.paused) {
				setPlayAudioButton(false)
			} else {
				setPlayAudioButton(true)
			}

			frameId = requestAnimationFrame(() => {
				renderNextFrame()
			})
		}

		renderNextFrame()

		return () => {
			if (frameId !== null) {
				cancelAnimationFrame(frameId)
			}
		}
	}, [selectedAudio])

	function handleAudioPlay() {
		if (!audioRef.current) {
			return
		}
		if (audioRef.current.paused) {
			audioRef.current
				.play()
				.catch(() => console.error("Cannot play"))
		} else {
			audioRef.current.pause()
		}

		if (selectedAudio !== audioRef.current) {
			selectedAudio?.pause()
		}
		setSelectedAudio && setSelectedAudio(audioRef.current)
	}

	if (
		preview_url === null ||
		selector.type !== "voices" ||
		id === null ||
		tags === null
	) {
		return <></>
	}

	return (
		<div
			key={id}
			onClick={() => {
				selector.action(id, name, tags, preview_url, gender)
				setSelectedAudio(null)
				onClick && onClick()
			}}
			className={clsx(
				isSelected && "bg-blue-100",
				"group items-center gap-[8px] p-[4px] hover:bg-blue-100",
				"flex w-full cursor-pointer rounded-[4px]",
			)}>
			<button
				onClick={(e) => {
					e.stopPropagation()
					handleAudioPlay()
				}}
				className={clsx(
					playAudioButton
						? "bg-color-chat-alpha"
						: "bg-blue-300",
					"shrink-0 rounded-full p-[7px]",
				)}>
				{playAudioButton ? (
					<img
						src={assetUrl("/general/pause-chat.png")}
						alt="play icon"
						className="h-[15px] w-[15px]"
					/>
				) : (
					<img
						src={assetUrl("/general/play.png")}
						alt="play icon"
						className="h-[15px] w-[15px]"
					/>
				)}
			</button>

			<div className="flex w-[70%] flex-col items-start">
				<span className="text-[13px] font-500 text-blue-600">
					{name}
				</span>

				{tags && (
					<div className="flex w-full items-center truncate">
						{tags.map((l, index) => (
							<div
								key={index}
								className="flex items-center">
								<span className="text-[13px] font-400 text-blue-600">
									{l}
								</span>
								{index + 1 !== tags?.length && (
									<div className="mx-[4px] h-[4px] w-[4px] rounded-full bg-blue-300" />
								)}
							</div>
						))}
					</div>
				)}
			</div>

			<audio className="hidden" ref={audioRef} controls>
				<source src={preview_url} type="audio/mpeg" />
				Your browser does not support the audio element.
			</audio>
		</div>
	)
}

const MAX_ALLOWED_DISTANCE = 0.0001
function getNextVisibleChild(
	container: HTMLDivElement,
): HTMLElement | null {
	const parentBounds = container.getBoundingClientRect()
	const parentX = parentBounds.x
	const parentWidth = parentBounds.width

	for (const child of container.childNodes) {
		if (child instanceof HTMLElement) {
			const { x, width } = child.getBoundingClientRect()
			if (
				x + width - MAX_ALLOWED_DISTANCE >
				parentX + parentWidth
			) {
				return child
			}
		}
	}

	return null
}

function getPrevVisibleChild(
	container: HTMLDivElement,
): HTMLButtonElement | null {
	const parentBounds = container.getBoundingClientRect()
	const parentX = parentBounds.x
	let last: HTMLButtonElement | null = null
	for (const child of container.childNodes) {
		if (child instanceof HTMLButtonElement) {
			const { x } = child.getBoundingClientRect()
			if (x + MAX_ALLOWED_DISTANCE > parentX) {
				return last
			}
			last = child
		}
	}

	return null
}

function NoActorImage() {
	return (
		<svg
			width="29"
			height="28"
			viewBox="0 0 29 28"
			fill="none"
			xmlns="http://www.w3.org/2000/svg">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M10.3384 14.884C8.67526 13.6042 7.60352 11.5938 7.60352 9.33301C7.60352 5.46701 10.7375 2.33301 14.6035 2.33301C17.4903 2.33301 19.9689 4.08045 21.0393 6.57525C21.2266 7.01174 21.1319 7.50938 20.8489 7.89083L19.6683 9.48206C19.5514 9.63966 19.2702 9.52925 19.2702 9.33301C19.2702 6.75568 17.1808 4.66634 14.6035 4.66634C12.0262 4.66634 9.93685 6.75568 9.93685 9.33301C9.93685 11.8833 11.9826 13.9559 14.5227 13.999C14.5766 13.9986 14.6305 13.9986 14.6843 13.999C14.9618 13.9943 15.2333 13.9654 15.4969 13.9143C15.8064 13.8543 16.1199 14.2648 15.932 14.518L14.8441 15.9844C14.6813 16.2038 14.4221 16.3377 14.1489 16.3342C13.9592 16.3366 13.9115 16.345 13.8251 16.3603C13.7758 16.369 13.7139 16.3799 13.606 16.3932C12.2952 16.5545 11.043 17.0313 9.95684 17.7828C8.87071 18.5343 7.98304 19.538 7.36997 20.7079C6.7569 21.8777 6.4367 23.1788 6.43685 24.4996C6.43692 25.1439 5.91464 25.6663 5.27031 25.6664C4.62598 25.6664 4.10359 25.1442 4.10352 24.4998C4.10333 22.8017 4.515 21.1289 5.30324 19.6248C6.09148 18.1207 7.23276 16.8302 8.62922 15.864C9.15672 15.499 9.71468 15.1844 10.2957 14.9231C10.3096 14.9097 10.3238 14.8967 10.3384 14.884ZM20.4782 17.7863C20.1741 18.1961 20.2329 18.7651 20.5671 19.1508C20.9783 19.6254 21.3349 20.1476 21.6286 20.7079C22.2416 21.8777 22.5618 23.1788 22.5617 24.4996C22.5616 25.1439 23.0839 25.6663 23.7282 25.6664C24.3726 25.6664 24.8949 25.1442 24.895 24.4998C24.8952 22.8017 24.4835 21.1289 23.6953 19.6248C23.268 18.8095 22.7371 18.057 22.1179 17.3848C21.7212 16.9541 21.041 17.0277 20.6921 17.498L20.4782 17.7863ZM26.5026 7.28722C26.9054 6.78429 26.8242 6.05007 26.3213 5.6473C25.8183 5.24453 25.0841 5.32574 24.6813 5.82867L11.3698 23.7704C10.9671 24.2734 11.0483 25.0076 11.5512 25.4103C12.0541 25.8131 12.7884 25.7319 13.1911 25.229L26.5026 7.28722Z"
				fill="var(--icon-color)"
			/>
		</svg>
	)
}
